/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

// 3rd party libraries
import React from 'react';
import {
  CheckCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import AppConstants from './AppConstants';
import { WorkerState } from '../pages/PlaygroundV3/components/WorkerStatusInfo/interface';
import { MethodLearningState } from '../components/MiniPlayground/interface';

const WorkerStateUtil = {
  getFriendlyWorkerState(state: string) {
    let mappedState = state;
    Object.values(AppConstants.RUN_STATE_MAP).forEach((v) => {
      if (v.value.toLowerCase() === state.toLowerCase()) {
        mappedState = v.label;
      }
    });
    return mappedState;
  },

  getWorkerStateIcon(state: string) {
    switch (state) {
      case 'creating':
      case 'created':
      case 'processing':
        return <LoadingOutlined />;
      case 'waiting_for_user_input':
      case 'waiting_for_user':
      case 'error':
        return <WarningOutlined style={{ color: 'orange' }} />;
      case 'done':
      case 'idle': // this is a state that exists for some old workers but has been deprecated
        return <CheckCircleOutlined style={{ color: 'green' }} />;
      case 'user stopped':
        return <MinusCircleOutlined style={{ color: 'gray' }} />;
      default:
        // We hit an unsupported case.
        return <QuestionCircleOutlined style={{ color: 'gray' }} />;
    }
  },

  getWorkerStateLifecycle(
    status: WorkerState
  ): Record<MethodLearningState, boolean> {
    return {
      [MethodLearningState.LOADING]: [
        WorkerState.CREATING,
        WorkerState.CREATED,
        WorkerState.PROCESSING
      ].includes(status),
      [MethodLearningState.FINISHED]: [WorkerState.DONE].includes(status),
      [MethodLearningState.FAILED]: [
        WorkerState.ERROR,
        WorkerState.WAITING_FOR_USER_INPUT
      ].includes(status)
    };
  }
};

export default WorkerStateUtil;
